export const FONT_PAIRINGS = [
  ['Playfair Display', 'Lato'], ['Cormorant Garamond', 'Montserrat'], ['Libre Baskerville', 'Source Sans 3'],
  ['EB Garamond', 'Karla'], ['Abril Fatface', 'Work Sans'], ['Josefin Sans', 'Inter'],
  ['Instrument Serif', 'Outfit'], ['Bodoni Moda', 'DM Sans'], ['Crimson Pro', 'Mulish'],
  ['Lora', 'Poppins'], ['Prata', 'Manrope'], ['Old Standard TT', 'Raleway'],
];

export const PAGE_TYPES = [
  { value: 'about', label: 'About / Bio' },
  { value: 'portfolio', label: 'Portfolio / Gallery' },
  { value: 'services', label: 'Services / Investment' },
  { value: 'contact', label: 'Contact / Inquire' },
  { value: 'gallery', label: 'Client Gallery (Embed)' },
  { value: 'content', label: 'Custom' },
];

export const SECTION_SUGGESTIONS = {
  nav: [
    'Make the nav transparent with backdrop blur on scroll',
    'Add a gold underline that draws itself on link hover',
    'Switch to a hamburger menu with fullscreen overlay',
    'Make nav links uppercase with 0.2em letter-spacing',
    'Add a subtle shadow when scrolled',
    'Make the logo text italic with a gold accent',
    'Shrink the nav height on scroll for a sleeker feel',
    'Add a thin gold line below the entire nav',
    'Center the logo with links split on either side',
    'Add smooth fade-in animation to nav on page load',
    'Use a monospace font for the nav links',
    'Make links disappear on scroll down, reappear on scroll up',
    'Add a subtle gradient background to the nav',
    'Put the CTA button in a contrasting color from the links',
    'Add hover color transition with custom cubic-bezier easing',
  ],
  hero: [
    'Make the hero heading 30% bigger with tighter letter-spacing',
    'Add a Ken Burns slow zoom on the background image',
    'Add film grain texture at 4% opacity with animated jitter',
    'Add a parallax effect so the image moves slower than text on scroll',
    'Add a deep vignette — dark edges fading to center',
    'Make the text enter with a blur-to-sharp focus pull animation',
    'Add floating golden light particles in the background',
    'Make the background image black and white with high contrast',
    'Add a scroll indicator animation at the bottom',
    'Change the CTA button to have a pulsing gold glow',
    'Split the hero — text left, image right in asymmetric layout',
    'Add a dark gradient overlay from bottom to top',
    'Make the subtext appear with a staggered word-by-word animation',
    'Add a thin decorative gold line between heading and subtext',
    'Use a video background instead of a static image',
  ],
  about: [
    'Rewrite the copy to be more personal — first person, warm tone',
    'Add a decorative gold line divider above the heading',
    'Make the image larger with a subtle CSS filter treatment',
    'Switch to an asymmetric layout — 60/40 split',
    'Add a pull quote in large italic serif between paragraphs',
    'Add a subtle paper texture to the background',
    'Make the section heading use a script/handwriting style accent',
    'Add entrance animation with staggered delays for each element',
    'Add a small "ABOUT" label in tiny uppercase above the heading',
    'Make the image have a slight rotation for a casual feel',
    'Add a border-left accent line in the brand color',
    'Use a different background color to distinguish from other sections',
    'Add hover zoom effect on the about photo',
    'Include approach values as small icon cards below the bio',
    'Make the paragraph text slightly larger with generous line height',
  ],
  gallery: [
    'Switch to a masonry grid with varying image heights',
    'Add zoom + brightness shift on hover with custom easing',
    'Make images black and white, revealing color on hover',
    'Add image overlay with couple names on hover',
    'Switch to a horizontal scroll gallery section',
    'Add a lightbox effect — click to see full size',
    'Increase the gap between images for a gallery wall feel',
    'Add a span-2 large image to break up the grid rhythm',
    'Add film-style borders around each image',
    'Use CSS filters to give all images a consistent warm tone',
    'Add a slow fade-in reveal as images enter the viewport',
    'Number the images like archival prints (001, 002, etc)',
    'Add a caption below each image with venue/location',
    'Make the grid asymmetric — different sizes create visual tension',
    'Add a subtle drop shadow that grows on hover',
  ],
  services: [
    'Rename to "Investment" for a more premium feel',
    'Add pricing tiers with elegant card borders',
    'Add a gold accent line above each service card',
    'Make cards lift with shadow on hover',
    'Add a "Most Popular" badge to the middle tier',
    'Use alternating background colors for visual rhythm',
    'Add an elegant "Starting at" with large price typography',
    'Include small icons for each included feature',
    'Add custom bullet points instead of standard list dots',
    'Make the CTA button different for each tier',
    'Add a subtle background pattern or texture',
    'Use a 3-column layout on desktop, stack on mobile',
    'Add a comparison table below the cards',
    'Make the heading italic for elegance',
    'Add entrance animation with staggered card reveals',
  ],
  testimonial: [
    'Add a large decorative quotation mark in the accent color',
    'Use a serif italic font for the quote text',
    'Add a circular portrait image of the couple',
    'Create a horizontal carousel of testimonials',
    'Add a star rating above each testimonial',
    'Use a different background color for contrast',
    'Add a "Read more" expandable for long testimonials',
    'Make testimonials appear as cards with subtle borders',
    'Add a slide-in animation from the left on scroll',
    'Include the wedding date and venue below the names',
    'Add a decorative separator between testimonials',
    'Use alternating alignment (left, right) for visual interest',
    'Add a subtle background image with heavy overlay',
    'Make the section full-width with generous padding',
    'Add a "View all reviews" link at the bottom',
  ],
  contact: [
    'Add a warm personal note above the form — "I\'d love to hear from you"',
    'Style the form inputs with bottom-border-only (no boxes)',
    'Add a gradient or textured background section',
    'Include studio location with an embedded map',
    'Add social media icons in the accent color',
    'Make the submit button full-width with hover animation',
    'Add a phone number with click-to-call link',
    'Include "Expected response time: within 24 hours"',
    'Use a two-column layout — info left, form right',
    'Add a decorative separator between info and form',
    'Include a "Preferred contact method" dropdown',
    'Add placeholder text that reads like conversation',
    'Make the section warm and inviting, not corporate',
    'Add an availability calendar or "Now booking 2026" badge',
    'Include a brief FAQ (turnaround, deposits, travel)',
  ],
  footer: [
    'Add a subtle grain texture overlay',
    'Include social media links as icons',
    'Add a "Back to top" animated arrow',
    'Use a darker background color for contrast',
    'Include studio address and phone number',
    'Add a small logo or wordmark',
    'Use a two-column layout — info left, links right',
    'Add a subtle border-top in the accent color',
    'Include copyright with dynamic year',
    'Add a "Built with GoldenHour" credit link',
    'Use smaller, lighter font weight for an elegant feel',
    'Add a newsletter signup form',
    'Include business hours or availability',
    'Add a decorative wave or curve above the footer',
    'Make links change color on hover with smooth transition',
  ],
  content: [
    'Add a heading with accent-colored underline',
    'Use a clean, readable body font with generous spacing',
    'Add an entrance animation for this section',
    'Use a different background color for visual rhythm',
    'Add a thin border or line accent',
    'Include an image or visual element',
    'Make the heading italic for emphasis',
    'Add a CTA button or link at the bottom',
    'Use a pull quote or highlighted text block',
    'Add a decorative gold line divider',
    'Increase the font size for better readability',
    'Add hover effects to interactive elements',
    'Use CSS filters on any images for a cohesive look',
  ],
};
